import type { GraphQLAuditItem, GraphQLAuditDecision, ClassificationResult } from '@internal-audit/domain/graphql-types';
import { useCallback } from 'react';

import { useUpsertAuditDecision } from '../../../hooks/useUpsertAuditDecision';

import type { DecisionFormData } from './useDecisionFormData';

interface UseAuditDecisionActionsProps {
  selectedItem: GraphQLAuditItem | null;
  currentUserDecision?: GraphQLAuditDecision;
  onSave: (decision: GraphQLAuditDecision) => void;
  onClose: () => void;
  isSessionCompleted: boolean;
  getClassificationResults: (item: GraphQLAuditItem | null) => ClassificationResult[];
}

/**
 * Custom hook to manage audit decision actions (save, mark all correct)
 * Extracted to reduce component complexity and improve maintainability
 */
export function useAuditDecisionActions({
  selectedItem,
  currentUserDecision,
  onSave,
  onClose,
  isSessionCompleted,
  getClassificationResults,
}: UseAuditDecisionActionsProps) {
  // Use the clean hook that encapsulates GraphQL logic with automatic cache updates
  const { upsertAuditDecision, loading } = useUpsertAuditDecision({
    item: selectedItem
      ? {
          id: selectedItem.id,
          auditSessionId: selectedItem.auditSessionId,
        }
      : { id: '', auditSessionId: '' },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    existingDecision: currentUserDecision as any, // Type assertion needed due to domain type mismatch
  });

  // Helper function to calculate smart inference values
  const calculateSmartInference = useCallback((decisionData: DecisionFormData) => {
    const validTriggeredAssessments = decisionData.triggeredPolicyAssessments.filter(a => a.isCorrect !== null);
    const validNonTriggeredAssessments = decisionData.nonTriggeredPolicyAssessments.filter(a => a.isCorrect !== null);

    const hasLabelCorrections = decisionData.correctedLabels.length > 0;
    const hasPolicyDisagreements = validTriggeredAssessments.some(a => a.isCorrect === false) || validNonTriggeredAssessments.some(a => a.isCorrect === false);

    return {
      validTriggeredAssessments,
      validNonTriggeredAssessments,
      // Labels: true if no corrections (agreeing), false if corrections provided (disagreeing)
      inferredLabelsCorrect: hasLabelCorrections ? false : true,
      // Policies: true if no disagreements (agreeing), false if any disagreements
      inferredPoliciesCorrect: hasPolicyDisagreements ? false : true,
    };
  }, []);

  // Helper function to get corrected text only if marked incorrect
  const getCorrectedText = useCallback((isCorrect: boolean | null, correctedText: string): string | null => (isCorrect === false ? correctedText : null), []);

  // Helper function to save decision data
  const saveDecisionData = useCallback(
    async (decisionData: DecisionFormData) => {
      if (!selectedItem) return;

      try {
        const originalLabels = getClassificationResults(selectedItem).map((result: ClassificationResult) => ({
          name: result.name,
          severity: result.severity,
        }));

        const { validTriggeredAssessments, validNonTriggeredAssessments, inferredLabelsCorrect, inferredPoliciesCorrect } =
          calculateSmartInference(decisionData);

        // Use explicit assessments when provided, fallback to smart inference
        const finalLabelsCorrect = decisionData.labelsCorrect !== null ? decisionData.labelsCorrect : inferredLabelsCorrect;
        const finalPoliciesCorrect = decisionData.policiesCorrect !== null ? decisionData.policiesCorrect : inferredPoliciesCorrect;

        const result = await upsertAuditDecision({
          // Required fields for simplified external QA workflow
          auditItemId: selectedItem.id,
          auditSessionId: selectedItem.auditSessionId,
          originalLabels,
          labelsCorrect: finalLabelsCorrect,
          policiesCorrect: finalPoliciesCorrect,

          // Optional fields for backward compatibility and advanced usage
          existingDecisionId: currentUserDecision?.id,
          shouldDelete: false,
          languageCorrect: decisionData.languageCorrect,
          transcriptionCorrect: decisionData.transcriptionCorrect,
          correctedTranscription: getCorrectedText(decisionData.transcriptionCorrect, decisionData.correctedTranscription),
          ocrCorrect: decisionData.ocrCorrect,
          correctedOcr: getCorrectedText(decisionData.ocrCorrect, decisionData.correctedOcr),
          correctedLabels: decisionData.correctedLabels.map(labelKey => {
            const lastHyphen = labelKey.lastIndexOf('-');
            const name = labelKey.substring(0, lastHyphen);
            const severity = parseInt(labelKey.substring(lastHyphen + 1), 10);

            return { name, severity };
          }),
          comments: decisionData.comments || null,
          triggeredPolicyAssessments: validTriggeredAssessments.map(a => ({
            ...a,
            isCorrect: a.isCorrect as boolean, // Type assertion safe due to filtering
          })),
          nonTriggeredPolicyAssessments: validNonTriggeredAssessments.map(a => ({
            ...a,
            isCorrect: a.isCorrect as boolean, // Type assertion safe due to filtering
          })),
        });

        if (result.success && result.data) {
          onSave(result.data as GraphQLAuditDecision);
          // Only close the panel after successful mutation completion
          onClose();
        }
      } catch {
        // Don't close the panel on error so user can retry
      }
    },
    [selectedItem, currentUserDecision, upsertAuditDecision, onSave, onClose, calculateSmartInference, getClassificationResults, getCorrectedText]
  );

  const handleSave = useCallback(
    async (formData: DecisionFormData) => {
      if (!selectedItem || isSessionCompleted) return;

      await saveDecisionData(formData);
    },
    [selectedItem, saveDecisionData, isSessionCompleted]
  );

  // eslint-disable-next-line complexity -- hasPolicies check adds one branch to existing Mark All Correct initialization logic
  const createMarkAllCorrectData = useCallback((item: GraphQLAuditItem): DecisionFormData => {
    const hasPolicies = (item.contentSnapshot?.triggeredPolicies?.length ?? 0) > 0 || (item.contentSnapshot?.nonTriggeredPolicies?.length ?? 0) > 0;

    return {
      // Mark ALL fields as correct for "Mark All Correct" functionality
      languageCorrect: true,
      transcriptionCorrect: true,
      correctedTranscription: '',
      ocrCorrect: true,
      correctedOcr: '',
      correctedLabels: [], // Don't create corrected labels - just mark as correct
      comments: '',
      // Mark overall assessments as correct for "Mark All Correct" functionality
      labelsCorrect: true,
      policiesCorrect: hasPolicies ? true : null, // Only set policies correct when item has policies
      triggeredPolicyAssessments:
        item.contentSnapshot?.triggeredPolicies?.map(policy => ({
          policyId: policy.id,
          policyName: policy.name,
          isCorrect: true,
          comments: '',
        })) || [],
      nonTriggeredPolicyAssessments:
        item.contentSnapshot?.nonTriggeredPolicies?.map(policy => ({
          policyId: policy.id,
          policyName: policy.name,
          isCorrect: true,
          comments: '',
        })) || [],
    };
  }, []);

  const handleMarkAllCorrect = useCallback(
    async (setFormData: (data: DecisionFormData) => void) => {
      if (!selectedItem || isSessionCompleted) return;

      const markAllCorrectData = createMarkAllCorrectData(selectedItem);

      // Only update form data - don't automatically save
      setFormData(markAllCorrectData);
    },
    [selectedItem, isSessionCompleted, createMarkAllCorrectData]
  );

  return {
    loading,
    handleSave,
    handleMarkAllCorrect,
  };
}
